import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { StockLevel, MaterialCategory } from '@models/warehouse.model';
import { calculateStockStatus } from '@components/atoms/StockBadge';
import { generateStockOverviewPdfFilename } from './pdfFilename';

interface StockOverviewPdfOptions {
  stockLevels: StockLevel[];
  categories: MaterialCategory[];
  warehouseName?: string;
}

// Color constants (matching Tailwind theme)
const PRIMARY: [number, number, number] = [79, 70, 229]; // indigo-600
const HEADER_BG: [number, number, number] = [249, 250, 251]; // gray-50
const LOW_STOCK_BG: [number, number, number] = [255, 251, 235]; // amber-50
const OUT_OF_STOCK_BG: [number, number, number] = [254, 242, 242]; // red-50
const TEXT_PRIMARY: [number, number, number] = [17, 24, 39]; // gray-900
const TEXT_SECONDARY: [number, number, number] = [107, 114, 128]; // gray-500
const GREEN: [number, number, number] = [22, 163, 74]; // green-600
const AMBER: [number, number, number] = [217, 119, 6]; // amber-600
const RED: [number, number, number] = [220, 38, 38]; // red-600

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

const formatNumber = (value: number) =>
  new Intl.NumberFormat('en-US').format(value);

export function generateStockOverviewPdf({
  stockLevels,
  categories,
  warehouseName,
}: StockOverviewPdfOptions): void {
  const doc = new jsPDF('landscape', 'mm', 'letter');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;

  const getCategoryName = (level: StockLevel): string => {
    // Try from catalog.category first, then fall back to categories list
    if (level.catalog?.category?.name) return level.catalog.category.name;
    if (level.catalog?.categoryId) {
      const cat = categories.find((c) => c.categoryId === level.catalog?.categoryId);
      return cat?.name || '-';
    }
    return '-';
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'in_stock': return 'In Stock';
      case 'low_stock': return 'Low Stock';
      case 'out_of_stock': return 'Out of Stock';
      default: return '-';
    }
  };

  // Compute stats
  const totalItems = stockLevels.length;
  let totalValue = 0;
  let lowStockCount = 0;
  let outOfStockCount = 0;

  stockLevels.forEach((level) => {
    const onHand = Number(level.quantityOnHand) || 0;
    const reserved = Number(level.quantityReserved) || 0;
    const available = onHand - reserved;
    const avgCost = Number(level.averageCost) || 0;
    totalValue += onHand * avgCost;

    const status = calculateStockStatus(available, level.catalog?.reorderPoint);
    if (status === 'low_stock') lowStockCount++;
    if (status === 'out_of_stock') outOfStockCount++;
  });

  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // ========================
  // HEADER BAR
  // ========================
  doc.setFillColor(...PRIMARY);
  doc.rect(0, 0, pageWidth, 20, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  const title = warehouseName
    ? `Stock Overview — ${warehouseName}`
    : 'Stock Overview';
  doc.text(title, margin, 13);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(today, pageWidth - margin, 13, { align: 'right' });

  // ========================
  // SUMMARY STATS (4 cards)
  // ========================
  const statsY = 28;
  const cardGap = 4;
  const cardWidth = (pageWidth - margin * 2 - cardGap * 3) / 4;
  const cardHeight = 18;

  const stats = [
    { label: 'Total Items', value: formatNumber(totalItems), color: PRIMARY },
    { label: 'Inventory Value', value: formatCurrency(totalValue), color: GREEN },
    { label: 'Low Stock', value: formatNumber(lowStockCount), color: AMBER },
    { label: 'Out of Stock', value: formatNumber(outOfStockCount), color: RED },
  ];

  stats.forEach((stat, i) => {
    const x = margin + i * (cardWidth + cardGap);

    doc.setFillColor(245, 245, 250);
    doc.roundedRect(x, statsY, cardWidth, cardHeight, 2, 2, 'F');

    doc.setTextColor(...TEXT_SECONDARY);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(stat.label, x + 4, statsY + 7);

    doc.setTextColor(...stat.color);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(stat.value, x + 4, statsY + 14);
  });

  // ========================
  // TABLE
  // ========================
  const tableColumns = [
    { header: 'Material', dataKey: 'name' },
    { header: 'SKU', dataKey: 'sku' },
    { header: 'Category', dataKey: 'category' },
    { header: 'On Hand', dataKey: 'onHand' },
    { header: 'Reserved', dataKey: 'reserved' },
    { header: 'Available', dataKey: 'available' },
    { header: 'Status', dataKey: 'status' },
  ];

  const tableRows = stockLevels.map((level) => {
    const onHand = Number(level.quantityOnHand) || 0;
    const reserved = Number(level.quantityReserved) || 0;
    const available = onHand - reserved;
    const status = calculateStockStatus(available, level.catalog?.reorderPoint);
    const unit = level.catalog?.unit || 'units';

    return {
      name: level.catalog?.name || 'Unknown',
      sku: level.catalog?.sku || '-',
      category: getCategoryName(level),
      onHand: `${formatNumber(onHand)} ${unit}`,
      reserved: formatNumber(reserved),
      available: formatNumber(available),
      status: getStatusLabel(status),
      _stockStatus: status,
    };
  });

  autoTable(doc, {
    startY: statsY + cardHeight + 8,
    columns: tableColumns,
    body: tableRows,
    margin: { left: margin, right: margin },
    styles: {
      fontSize: 8,
      cellPadding: 3,
      textColor: TEXT_PRIMARY,
      lineColor: [229, 231, 235],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: HEADER_BG,
      textColor: TEXT_SECONDARY,
      fontStyle: 'bold',
      fontSize: 7,
      halign: 'left',
    },
    columnStyles: {
      onHand: { halign: 'right' },
      reserved: { halign: 'right' },
      available: { halign: 'right' },
      status: { halign: 'center' },
    },
    alternateRowStyles: {
      fillColor: [255, 255, 255],
    },
    didParseCell: (data) => {
      if (data.section === 'body') {
        const row = tableRows[data.row.index];
        if (!row) return;

        if (row._stockStatus === 'low_stock') {
          data.cell.styles.fillColor = LOW_STOCK_BG;
        } else if (row._stockStatus === 'out_of_stock') {
          data.cell.styles.fillColor = OUT_OF_STOCK_BG;
        }

        if (data.column.dataKey === 'status') {
          if (row._stockStatus === 'in_stock') {
            data.cell.styles.textColor = GREEN;
          } else if (row._stockStatus === 'low_stock') {
            data.cell.styles.textColor = AMBER;
          } else if (row._stockStatus === 'out_of_stock') {
            data.cell.styles.textColor = RED;
          }
          data.cell.styles.fontStyle = 'bold';
        }
      }
    },
    didDrawPage: () => {
      const pageNumber = doc.getCurrentPageInfo().pageNumber;

      doc.setDrawColor(229, 231, 235);
      doc.setLineWidth(0.5);
      doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);

      doc.setFontSize(7);
      doc.setTextColor(...TEXT_SECONDARY);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated on ${today}`, margin, pageHeight - 7);
      doc.text(
        `Page ${pageNumber} of {total_pages}`,
        pageWidth - margin,
        pageHeight - 7,
        { align: 'right' },
      );
    },
  });

  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.putTotalPages('{total_pages}');
  }

  const filename = generateStockOverviewPdfFilename();
  doc.save(filename);
}
